/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-nested-ternary */
import { useEffect, useMemo } from 'react';
import { Countries } from '@constants/enums';
import { Button } from '@efexteam/components';
import FileInputV3 from '@efex-components/EfexDS/FileInput/FileInput';
import useFileService from '@efex-components/EfexDS/FileInput/useFileService';
import TaxIdInput from '@efex-components/EfexDS/Inputs/TaxIdInput/TaxIdInput';
import {
  TaxIdInputPropsType,
  TaxIdValidation,
} from '@efex-components/EfexDS/Inputs/TaxIdInput/types';
import { OnboardingDocumentTypeIds } from '@efex-services/documents/types';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import {
  Alert,
  FormControl,
  Grid,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import clsx from 'clsx';
import { DateTime } from 'luxon';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { IOnboardingDocuments } from '@efex-services/documents/documentsSvc';
import { icon_check_circle_green } from '../../../../assets';
import NewCompanyDocuments from '../components/NewCompanyDocuments/NewCompanyDocuments';
import { onlyNumbersRegex, onlyTextRegex, websiteRegex } from '../constants';
import { useFormData } from '../context';

const getDefaultValues = (data: any) => ({
  website: data.website,
  addressLine1: data.addressLine1,
  addressLine2: data.addressLine2,
  city: data.city,
  state: data.state,
  postCode: data.postCode,
  taxId: data.taxId,
  industryCode: data.industryCode,
  hasForeignOperations: data.hasForeignOperations,
  isCompanyOperating: data.isCompanyOperating,
  incorporation: data.incorporation,
});

interface ICompanyDocumentation {
  isDisabledEdit?: boolean;
  loading?: boolean;
  nextFormStep: (data: any) => void;
}

export type FormularyDataUSA = {
  website?: string;
  addressLine1: string;
  addressLine2: string;
  city?: string;
  state?: string;
  postCode?: number;
  taxId: string;
  taxIdType: 'EIN' | 'SSN';
  incorporation?: DateTime;
  constitutionFile?: File;
  certificateFile?: File;
  satFile?: File;
  neighborhood?: string;
  businessSectorId?: string;
  resourceStatament?: boolean;
  resourceStatementOption?: string;
  legalRepresentativeStatament?: boolean;
  companyDocumentType?: 'passport' | 'personalId' | 'driversLicence';
  industryCode?: string;
  effectiveAt?: string;
  expiredAt?: string;
  number?: string;
  gender?: string;
  isCompanyOperating: boolean | null;
  hasForeignOperations: boolean | null;
};

const CompanyDocumentation = ({
  isDisabledEdit = false,
  loading,
  nextFormStep,
}: ICompanyDocumentation) => {
  const { t } = useTranslation();
  const { setFormValues, data } = useFormData();

  const UseFileServiceMethods = useFileService();

  const {
    onboardingDocuments,
    errorUploadDocuments,
    handleChangeFile,
    handleDeleteFile,
    handleDropChangeFile,
  } = UseFileServiceMethods;

  const methods = useForm<FormularyDataUSA>({
    mode: 'all',
    defaultValues: getDefaultValues(data),
  });

  const {
    handleSubmit,
    formState: { errors },
    register,
    setValue,
    control,
    watch,
    reset,
  } = methods;

  const FieldsWatched = useWatch({
    control,
    name: [
      'website',
      'addressLine1',
      'addressLine2',
      'city',
      'state',
      'taxId',
      'incorporation',
      'postCode',
    ],
  });

  const validateUsaTaxId = (values: string): string => {
    if (values.includes('-')) {
      return values;
    }
    return `${values.slice(0, 2)}-${values.slice(2, values.length - 1)}`;
  };

  const getRequiredDocuments = () => {
    if (!onboardingDocuments) {
      return {
        einDocument: [],
        constitutionFile: [],
        companyOperation: [],
        businessNature: [],
      };
    }

    return onboardingDocuments.reduce(
      (acc, element) => {
        const typeId = element.document.type.id;

        if (typeId === OnboardingDocumentTypeIds.einDocument) {
          acc.einDocument = [element];
        } else if (typeId === OnboardingDocumentTypeIds.constitutionFile) {
          acc.constitutionFile = [element];
        } else if (typeId === OnboardingDocumentTypeIds.companyOperation) {
          acc.companyOperation = [element];
        } else if (typeId === OnboardingDocumentTypeIds.businessNature) {
          acc.businessNature = [element];
        }

        return acc;
      },
      {
        einDocument: [] as IOnboardingDocuments[],
        constitutionFile: [] as IOnboardingDocuments[],
        companyOperation: [] as IOnboardingDocuments[],
        businessNature: [] as IOnboardingDocuments[],
      }
    );
  };

  const onSubmit = handleSubmit(async (values) => {
    const fixedTax = validateUsaTaxId(values.taxId);
    setFormValues({ ...data, ...values, taxId: fixedTax, isLastStep: false });
    nextFormStep({ ...data, ...values, taxId: fixedTax, isLastStep: false });
  });

  const allFormValues = watch();

  const getContinueButtonStatus = useMemo(() => {
    if (loading) return true;

    const requiredFields: (keyof FormularyDataUSA)[] = [
      'website',
      'addressLine1',
      'city',
      'taxId',
      'incorporation',
      'postCode',
    ];

    const allRequiredFieldsValid = requiredFields.every((field) => {
      const value = allFormValues[field];
      return value !== undefined && value !== null && value !== '';
    });

    const documents = getRequiredDocuments();

    const hasConstitutionFile = documents.constitutionFile.length > 0;
    const hasRequiredEinDocument = documents.einDocument.length > 0;
    const hasRequiredCompanyOperation = documents.companyOperation.length > 0;
    const hasRequiredBusinessNature =
      methods.getValues('hasForeignOperations') === false ||
      documents.businessNature.length > 0;

    const allDocumentsValid =
      hasConstitutionFile &&
      hasRequiredEinDocument &&
      hasRequiredCompanyOperation &&
      hasRequiredBusinessNature;

    return !allRequiredFieldsValid || !allDocumentsValid;
  }, [allFormValues, onboardingDocuments]);

  useEffect(() => {
    if (data && Object.keys(data).length > 0) {
      reset(getDefaultValues(data));
    }
  }, [data, reset]);

  const validatedTaxIdPlaceHolder = 'XX-XXXXXXX';

  return (
    <>
      <Typography variant="h4" className="!font-medium">
        {t('onbCompanyDocumentation')}
      </Typography>

      <FormProvider {...methods}>
        <form onSubmit={onSubmit} autoComplete="off">
          <Grid rowSpacing={2} columnSpacing={{ xs: 2, sm: 2, md: 3 }}>
            <Grid item xs={12} className="!my-[15px]">
              <FormControl fullWidth>
                <Typography variant="subtitle2">
                  {t(`onbWebsiteUSA`)}
                </Typography>
                <div className="flex w-full">
                  <TextField
                    autoComplete="off"
                    type="text"
                    className="custom-textfield w-[90%]"
                    size="small"
                    error={!!errors?.website}
                    placeholder={t(`onbWebsiteUSA`) as string}
                    {...register('website', {
                      required: true,
                      pattern: {
                        value: websiteRegex,
                        message: 'websiteError',
                      },
                    })}
                  />
                  {FieldsWatched[0] && FieldsWatched[0].length > 0 && (
                    <img
                      src={icon_check_circle_green}
                      alt=""
                      className="w-[25px] ml-[10px]"
                    />
                  )}
                </div>
                {errors &&
                  errors.website &&
                  errors.website.type === 'required' && (
                    <p className="text-orange-600 text-xs mt-1">
                      {t('defaultError')}
                    </p>
                  )}
                {errors &&
                  errors.website &&
                  errors.website.type === 'pattern' && (
                    <p className="text-orange-600 text-xs mt-1">
                      {t(errors.website.message ?? 'defaultError')}
                    </p>
                  )}
              </FormControl>
            </Grid>
            <Grid item xs={12} className="!my-[15px]">
              <FormControl fullWidth>
                <Typography variant="subtitle2">
                  {t(`onbCompanyAddressUSA`)}
                </Typography>
                <div className="flex w-full">
                  <TextField
                    autoComplete="off"
                    type="text"
                    className="custom-textfield w-[90%]"
                    size="small"
                    placeholder={t(`onbStakeAddress1`) as string}
                    error={!!errors.addressLine1}
                    helperText={errors.addressLine1?.message as string}
                    {...register('addressLine1', {
                      maxLength: {
                        value: 100,
                        message: t('addressTooLong'),
                      },
                      pattern: {
                        value: /^[a-zA-Z0-9\s]+$/,
                        message: t('addressInvalidCharacters'),
                      },
                      validate: {
                        noLeadingSpace: (value: string): any =>
                          !value.startsWith(' ') || t('addressStartsWithSpace'),
                        noTrailingSpace: (value: string): any =>
                          !value.endsWith(' ') || t('addressEndsWithSpace'),
                        noConsecutiveSpaces: (value: string): any =>
                          !/\s\s/.test(value) || t('addressConsecutiveSpaces'),
                      },
                    })}
                    inputProps={{
                      maxLength: 100,
                      pattern: '[a-zA-Z0-9\\s]+',
                      onInput: (e: React.FormEvent<HTMLInputElement>) => {
                        const input = e.currentTarget;
                        input.value = input.value.replace(
                          /[^a-zA-Z0-9\s]/g,
                          ''
                        );
                      },
                    }}
                  />
                  {FieldsWatched[2] && FieldsWatched[2].length > 0 && (
                    <img
                      src={icon_check_circle_green}
                      alt=""
                      className="w-[25px] ml-[10px]"
                    />
                  )}
                </div>
                {errors && errors.addressLine1 && (
                  <p className="text-orange-600 text-xs mt-1">
                    {t('defaultError')}
                  </p>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} className="!my-[15px]">
              <FormControl fullWidth>
                <div className="flex w-full">
                  <TextField
                    autoComplete="off"
                    type="text"
                    className="custom-textfield w-[90%]"
                    size="small"
                    placeholder={t(`onbStakeAddress2`) as string}
                    error={!!errors.addressLine2}
                    helperText={errors.addressLine2?.message as string}
                    {...register('addressLine2', {
                      maxLength: {
                        value: 100,
                        message: t('addressTooLong'),
                      },
                      pattern: {
                        value: /^[a-zA-Z0-9\s]+$/,
                        message: t('addressInvalidCharacters'),
                      },
                      validate: {
                        noLeadingSpace: (value: string): any =>
                          !value.startsWith(' ') || t('addressStartsWithSpace'),
                        noTrailingSpace: (value: string): any =>
                          !value.endsWith(' ') || t('addressEndsWithSpace'),
                        noConsecutiveSpaces: (value: string): any =>
                          !/\s\s/.test(value) || t('addressConsecutiveSpaces'),
                      },
                    })}
                    inputProps={{
                      maxLength: 100,
                      pattern: '[a-zA-Z0-9\\s]+',
                      onInput: (e: React.FormEvent<HTMLInputElement>) => {
                        const input = e.currentTarget;
                        input.value = input.value.replace(
                          /[^a-zA-Z0-9\s]/g,
                          ''
                        );
                      },
                    }}
                  />
                  {FieldsWatched[3] && FieldsWatched[3].length > 0 && (
                    <img
                      src={icon_check_circle_green}
                      alt=""
                      className="w-[25px] ml-[10px]"
                    />
                  )}
                </div>
              </FormControl>
            </Grid>
            <Grid container className="!my-[15px]">
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">{t(`city`)}</Typography>
                <FormControl fullWidth>
                  <div className="flex w-full">
                    <TextField
                      autoComplete="off"
                      type="text"
                      className="custom-textfield w-[90%]"
                      size="small"
                      placeholder={t(`city`) as string}
                      {...register('city', {
                        required: true,
                        pattern: {
                          value: onlyTextRegex,
                          message: '',
                        },
                      })}
                    />
                    {FieldsWatched[4] && FieldsWatched[4].length > 0 && (
                      <img
                        src={icon_check_circle_green}
                        alt=""
                        className="w-[25px] ml-[10px]"
                      />
                    )}
                  </div>
                  {errors && errors.city && errors.city.type === 'required' && (
                    <p className="text-orange-600 text-xs mt-1">
                      {t('defaultError')}
                    </p>
                  )}
                  {errors && errors.city && errors.city.type === 'pattern' && (
                    <p className="text-orange-600 text-xs mt-1">
                      {t('onbOnlyTextError')}
                    </p>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">{t(`state`)}</Typography>
                <FormControl fullWidth>
                  <div className="flex w-full">
                    <TextField
                      autoComplete="off"
                      type="text"
                      className="custom-textfield w-[80%]"
                      size="small"
                      placeholder={t(`state`) as string}
                      {...register('state', {
                        pattern: {
                          value: onlyTextRegex,
                          message: '',
                        },
                      })}
                    />
                    {FieldsWatched[5] && FieldsWatched[5].length > 0 && (
                      <img
                        src={icon_check_circle_green}
                        alt=""
                        className="w-[25px] ml-[10px]"
                      />
                    )}
                  </div>
                  {errors &&
                    errors.state &&
                    errors.state.type === 'required' && (
                      <p className="text-orange-600 text-xs mt-1">
                        {t('defaultError')}
                      </p>
                    )}
                  {errors &&
                    errors.state &&
                    errors.state.type === 'pattern' && (
                      <p className="text-orange-600 text-xs mt-1">
                        {t('onbOnlyTextError')}
                      </p>
                    )}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6} className="!mt-[15px]">
                <Typography variant="subtitle2">{t(`postCode`)}</Typography>
                <FormControl fullWidth>
                  <div className="flex w-full">
                    <TextField
                      autoComplete="off"
                      type="number"
                      className="custom-textfield w-[90%]"
                      size="small"
                      placeholder={t(`postCode`) as string}
                      {...register('postCode', {
                        required: true,
                        pattern: {
                          value: onlyNumbersRegex,
                          message: '',
                        },
                      })}
                    />
                    {FieldsWatched[7] && (
                      <img
                        src={icon_check_circle_green}
                        alt=""
                        className="w-[25px] ml-[10px]"
                      />
                    )}
                  </div>
                  {errors &&
                    errors.postCode &&
                    errors.postCode.type === 'required' && (
                      <p className="text-orange-600 text-xs mt-1">
                        {t('defaultError')}
                      </p>
                    )}
                  {errors &&
                    errors.postCode &&
                    errors.postCode.type === 'pattern' && (
                      <p className="text-orange-600 text-xs mt-1">
                        {t('onbOnlyNumberError')}
                      </p>
                    )}
                </FormControl>
              </Grid>
            </Grid>
            <Grid container>
              <Grid item xs={12} md={12}>
                <TaxIdInput
                  title="onbTaxIdUSA"
                  customPlaceholder={validatedTaxIdPlaceHolder}
                  validationType={TaxIdValidation.USA}
                  errors={errors}
                  countryOrigin={Countries.USA}
                  register={
                    register as unknown as TaxIdInputPropsType['register']
                  }
                  setValue={
                    setValue as unknown as TaxIdInputPropsType['setValue']
                  }
                  helperText={validatedTaxIdPlaceHolder}
                  textFieldClassName="custom-textfield w-[90%]"
                  errorClassName="text-xs mt-1"
                />
              </Grid>
              <Grid item xs={12} className="!mb-[15px]">
                <FileInputV3
                  isSingleFile
                  inputName="einDocument"
                  onChange={handleChangeFile}
                  onDropChange={handleDropChangeFile}
                  onDelete={handleDeleteFile}
                  error={errorUploadDocuments}
                  documents={getRequiredDocuments().einDocument}
                  title={t('onbEinDocument') as string}
                />
              </Grid>
            </Grid>
            <NewCompanyDocuments
              register={register}
              setValue={setValue}
              errors={errors}
              control={control}
              useFileService={UseFileServiceMethods}
            />
            <Grid item xs={12} className="!my-[15px]">
              <FormControl fullWidth>
                <Typography variant="subtitle2">
                  {t(`onbIncorporation`)}
                </Typography>
                <div className="flex w-full">
                  <Controller
                    name="incorporation"
                    rules={{ required: true }}
                    control={control}
                    render={({ field }) => (
                      <LocalizationProvider dateAdapter={AdapterLuxon}>
                        <DatePicker
                          {...field}
                          disableFuture
                          defaultValue={data?.incorporation || DateTime.now()}
                          className={clsx(
                            'w-full ',
                            FieldsWatched[6] ? '!mr-[0px]' : '!mr-[55px]',
                            'custom-textfield w-[90%]'
                          )}
                          onChange={(value) => {
                            if (!value) return;
                            field.onChange(value);
                          }}
                          slotProps={{
                            // eslint-disable-next-line react/no-unstable-nested-components
                            textField: (params) => (
                              <TextField {...params} size="small" fullWidth />
                            ),
                          }}
                        />
                      </LocalizationProvider>
                    )}
                  />
                  {FieldsWatched[6] && FieldsWatched[6] instanceof DateTime && (
                    <img
                      src={icon_check_circle_green}
                      alt=""
                      className="w-[25px] ml-[10px] mr-[20px]"
                    />
                  )}
                </div>
                {errors && errors.incorporation && (
                  <p className="text-orange-600 text-xs mt-1">
                    {t('defaultError')}
                  </p>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} className="!mb-[15px]">
              <FileInputV3
                isSingleFile
                inputName="constitutionFile"
                onChange={handleChangeFile}
                onDropChange={handleDropChangeFile}
                onDelete={handleDeleteFile}
                error={errorUploadDocuments}
                documents={getRequiredDocuments().constitutionFile}
                title={t('onbFormationCertificate') as string}
              />
            </Grid>
            {data.isCertificateUploaded ? (
              <Link
                href={`${data.isCertificateUploaded}`}
                variant="subtitle2"
                className="text-green-600"
                target="_blank"
              >
                {data.isCertificateUploaded.split('/').slice(-1)}
              </Link>
            ) : null}
          </Grid>
          <Grid item xs={12} className="flex justify-center gap-10">
            {!isDisabledEdit ? (
              <Button
                appearance="primary"
                size="large"
                type="submit"
                disabled={getContinueButtonStatus}
                endIcon={
                  loading ? (
                    <AutorenewIcon className="animate-spin" />
                  ) : undefined
                }
              >
                {t('continue')}
              </Button>
            ) : (
              <Alert severity="info">{t('kybStepTwoTitle')}</Alert>
            )}
          </Grid>
        </form>
      </FormProvider>
    </>
  );
};

export default CompanyDocumentation;
